/**
 * Glyph set for weapons, upgrades and HUD controls. Each glyph is drawn on a
 * 24-unit grid centred on (0,0) with a fill colour and an ink outline, so it
 * works both on medallions and directly on paper.
 */
import { INK, TAU, trace } from "./kit";

type Ctx = CanvasRenderingContext2D;
type Glyph = (c: Ctx, fill: string, ink: string) => void;

function shape(c: Ctx, pts: number[][], fill: string, ink: string) {
  trace(c, pts);
  c.fillStyle = fill;
  c.fill();
  c.strokeStyle = ink;
  c.stroke();
}
function round(c: Ctx, x: number, y: number, r: number, fill: string, ink: string) {
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  c.fillStyle = fill;
  c.fill();
  c.strokeStyle = ink;
  c.stroke();
}
function stroke(c: Ctx, pts: number[][], ink: string, width?: number) {
  c.beginPath();
  pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  const previous = c.lineWidth;
  if (width) c.lineWidth = width;
  c.strokeStyle = ink;
  c.stroke();
  c.lineWidth = previous;
}
function bolt(c: Ctx, fill: string, ink: string, s = 1) {
  shape(c, [[2 * s, -11 * s], [-7 * s, 2 * s], [-1 * s, 2 * s], [-3 * s, 11 * s], [7 * s, -3 * s], [1 * s, -3 * s]], fill, ink);
}
function flame(c: Ctx, fill: string, ink: string, s = 1) {
  c.beginPath();
  c.moveTo(0, -11 * s);
  c.bezierCurveTo(9 * s, -3 * s, 9 * s, 5 * s, 6 * s, 8 * s);
  c.quadraticCurveTo(0, 13 * s, -6 * s, 8 * s);
  c.bezierCurveTo(-9 * s, 4 * s, -6 * s, -2 * s, -3 * s, -4 * s);
  c.quadraticCurveTo(-2 * s, 0, 0, 1 * s);
  c.quadraticCurveTo(3 * s, -5 * s, 0, -11 * s);
  c.closePath();
  c.fillStyle = fill;
  c.fill();
  c.strokeStyle = ink;
  c.stroke();
}
function rocket(c: Ctx, fill: string, ink: string) {
  shape(c, [[0, -11], [4, -5], [4, 5], [8, 10], [-8, 10], [-4, 5], [-4, -5]], fill, ink);
  round(c, 0, -2, 1.8, ink, ink);
}
function heart(c: Ctx, fill: string, ink: string) {
  c.beginPath();
  c.moveTo(0, 9);
  c.bezierCurveTo(-12, 1, -9, -10, 0, -4);
  c.bezierCurveTo(9, -10, 12, 1, 0, 9);
  c.closePath();
  c.fillStyle = fill;
  c.fill();
  c.strokeStyle = ink;
  c.stroke();
}
function shield(c: Ctx, fill: string, ink: string) {
  shape(c, [[0, -10], [9, -6], [8, 3], [0, 10], [-8, 3], [-9, -6]], fill, ink);
}
function star(c: Ctx, fill: string, ink: string, points = 5, inner = 0.45, r = 11) {
  shape(
    c,
    Array.from({ length: points * 2 }, (_, i) => {
      const a = (i / (points * 2)) * TAU - Math.PI / 2,
        k = i % 2 ? r * inner : r;
      return [Math.cos(a) * k, Math.sin(a) * k];
    }),
    fill,
    ink,
  );
}
function arrowUp(c: Ctx, fill: string, ink: string, x = 0, y = 0, s = 1) {
  shape(c, [[x, y - 8 * s], [x + 6 * s, y - 1 * s], [x + 2.5 * s, y - 1 * s], [x + 2.5 * s, y + 8 * s], [x - 2.5 * s, y + 8 * s], [x - 2.5 * s, y - 1 * s], [x - 6 * s, y - 1 * s]], fill, ink);
}

const GLYPHS: Record<string, Glyph> = {
  // Main weapons
  straight(c, f, k) {
    for (const x of [-5, 0, 5]) {
      c.beginPath();
      c.rect(x - 1.8, -11, 3.6, 13);
      c.fillStyle = f;
      c.fill();
      c.strokeStyle = k;
      c.stroke();
    }
    round(c, 0, 6, 6, f, k);
    round(c, 0, 6, 2, k, k);
  },
  double(c, f, k) {
    for (const s of [-1, 1]) {
      c.save();
      c.translate(s * 5, 0);
      c.rotate(s * 0.25);
      shape(c, [[0, -11], [3, -6], [3, 8], [-3, 8], [-3, -6]], f, k);
      c.restore();
    }
  },
  fan(c, f, k) {
    for (let i = -2; i <= 2; i++) {
      const a = i * 0.38 - Math.PI / 2;
      round(c, Math.cos(a) * 9, Math.sin(a) * 9 + 4, 2.6, f, k);
    }
    round(c, 0, 8, 3.4, f, k);
  },
  laser(c, f, k) {
    c.beginPath();
    c.rect(-2.5, -12, 5, 16);
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    shape(c, [[-7, 3], [7, 3], [5, 10], [-5, 10]], f, k);
    round(c, 0, 6, 2.2, k, k);
  },
  flame(c, f, k) {
    flame(c, f, k);
  },
  rocket(c, f, k) {
    rocket(c, f, k);
  },
  rail(c, f, k) {
    c.beginPath();
    c.rect(-2, -12, 4, 22);
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    for (const y of [-6, 0, 6]) {
      c.beginPath();
      c.ellipse(0, y, 7, 2.4, 0, 0, TAU);
      c.strokeStyle = k;
      c.stroke();
    }
  },
  tesla(c, f, k) {
    for (const y of [5, 8]) stroke(c, [[-7, y], [7, y]], k);
    shape(c, [[-4, 10], [4, 10], [3, 2], [-3, 2]], f, k);
    bolt(c, f, k, 0.7);
  },
  // Side weapons
  wing(c, f, k) {
    for (const s of [-1, 1]) shape(c, [[s * 7, -8], [s * 11, 6], [s * 7, 4], [s * 3, 6]], f, k);
    shape(c, [[0, -11], [5, 8], [0, 5], [-5, 8]], f, k);
  },
  orbit(c, f, k) {
    c.beginPath();
    c.ellipse(0, 0, 11, 5, -0.4, 0, TAU);
    c.strokeStyle = k;
    c.stroke();
    round(c, 0, 0, 5, f, k);
    round(c, 9, -4, 2.6, f, k);
  },
  homing(c, f, k) {
    stroke(c, [[-9, 10], [-7, 0], [0, -4]], k);
    c.save();
    c.translate(4, -6);
    c.rotate(0.9);
    c.scale(0.6, 0.6);
    rocket(c, f, k);
    c.restore();
  },
  boomerang(c, f, k) {
    c.beginPath();
    c.arc(0, 2, 10, Math.PI * 1.05, Math.PI * 1.95);
    c.arc(0, 6, 7, Math.PI * 1.95, Math.PI * 1.05, true);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
  },
  split(c, f, k) {
    round(c, 0, 2, 6, f, k);
    for (const [x, y] of [[-8, -8], [8, -8], [0, -11]]) round(c, x, y, 2.4, f, k);
  },
  arc(c, f, k) {
    round(c, -8, 7, 3, f, k);
    round(c, 8, 7, 3, f, k);
    stroke(c, [[-8, 4], [-4, -6], [0, 2], [4, -8], [8, 4]], k, 3.4);
    stroke(c, [[-8, 4], [-4, -6], [0, 2], [4, -8], [8, 4]], f, 1.6);
  },
  // Upgrades
  attack(c, f, k) {
    c.beginPath();
    c.moveTo(0, -11);
    c.bezierCurveTo(8, -2, 9, 3, 7, 6);
    c.arc(0, 4, 7.2, 0.3, Math.PI - 0.3);
    c.bezierCurveTo(-9, 3, -8, -2, 0, -11);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
  },
  rate(c, f, k) {
    shape(c, [[0, -11], [9, -3], [5, -3], [0, -7], [-5, -3], [-9, -3]], f, k);
    shape(c, [[0, -2], [9, 6], [5, 6], [0, 2], [-5, 6], [-9, 6]], f, k);
  },
  critical(c, f, k) {
    star(c, f, k, 4, 0.35);
  },
  health: heart,
  regen(c, f, k) {
    heart(c, f, k);
    stroke(c, [[0, -3], [0, 4]], k, 2.4);
    stroke(c, [[-3.5, 0.5], [3.5, 0.5]], k, 2.4);
  },
  repair(c, f, k) {
    c.save();
    c.rotate(-0.8);
    c.beginPath();
    c.rect(-2.4, -3, 4.8, 14);
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    c.beginPath();
    c.arc(0, -6, 6, 0.6, Math.PI * 2 - 0.6 + Math.PI);
    c.lineTo(-2, -9);
    c.lineTo(2, -9);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.stroke();
    c.restore();
  },
  shield,
  barrier(c, f, k) {
    shield(c, f, k);
    stroke(c, [[-4, 1], [-1, 4], [5, -3]], k, 2.4);
  },
  armor(c, f, k) {
    for (const y of [-6, 0, 6]) shape(c, [[-9, y - 3], [9, y - 3], [7, y + 3], [-7, y + 3]], f, k);
  },
  experience(c, f, k) {
    c.beginPath();
    c.arc(0, -3, 7, Math.PI * 0.8, Math.PI * 2.2);
    c.lineTo(3, 6);
    c.lineTo(-3, 6);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    stroke(c, [[-3, 9], [3, 9]], k);
  },
  cooldown(c, f, k) {
    shape(c, [[-7, -11], [7, -11], [1, 0], [7, 11], [-7, 11], [-1, 0]], f, k);
    stroke(c, [[-3, 8], [3, 8]], k);
  },
  grace(c, f, k) {
    c.beginPath();
    c.moveTo(8, -10);
    c.quadraticCurveTo(-8, -6, -8, 10);
    c.quadraticCurveTo(6, 4, 8, -10);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    stroke(c, [[-8, 10], [4, -4]], k);
  },
  lanes(c, f, k) {
    for (const x of [-7, 0, 7]) arrowUp(c, f, k, x, 0, 0.7);
  },
  burst(c, f, k) {
    for (const y of [-7, 0, 7]) round(c, 0, y, 3.2, f, k);
  },
  pierce(c, f, k) {
    round(c, 0, 0, 6, f, k);
    stroke(c, [[-11, 8], [10, -9]], k, 2.6);
    shape(c, [[11, -10], [5, -8], [9, -4]], k, k);
  },
  size(c, f, k) {
    round(c, 3, -2, 8, f, k);
    round(c, -7, 7, 3, f, k);
  },
  velocity(c, f, k) {
    shape(c, [[10, 0], [2, -7], [2, -3], [-4, -3], [-4, 3], [2, 3], [2, 7]], f, k);
    for (const y of [-6, 0, 6]) stroke(c, [[-11, y], [-7, y]], k);
  },
  speed(c, f, k) {
    GLYPHS.velocity(c, f, k);
  },
  push(c, f, k) {
    for (const r of [4, 8, 12]) {
      c.beginPath();
      c.arc(0, 8, r, Math.PI * 1.2, Math.PI * 1.8);
      c.strokeStyle = k;
      c.lineWidth = 3.2;
      c.stroke();
      c.strokeStyle = f;
      c.lineWidth = 1.4;
      c.stroke();
    }
    c.lineWidth = 2;
  },
  blast(c, f, k) {
    star(c, f, k, 8, 0.55);
    round(c, 0, 0, 3, k, k);
  },
  chain(c, f, k) {
    bolt(c, f, k);
  },
  burn(c, f, k) {
    flame(c, f, k, 0.85);
  },
  frost(c, f, k) {
    for (let i = 0; i < 3; i++) {
      c.save();
      c.rotate((i * Math.PI) / 3);
      stroke(c, [[0, -11], [0, 11]], k, 4);
      stroke(c, [[0, -11], [0, 11]], f, 2);
      stroke(c, [[-3, -8], [0, -5], [3, -8]], f, 1.6);
      stroke(c, [[-3, 8], [0, 5], [3, 8]], f, 1.6);
      c.restore();
    }
  },
  ricochet(c, f, k) {
    stroke(c, [[-10, 9], [-4, -6], [2, 6], [8, -9]], k, 4.2);
    stroke(c, [[-10, 9], [-4, -6], [2, 6], [8, -9]], f, 2);
  },
  magnet(c, f, k) {
    c.beginPath();
    c.arc(0, -1, 8, Math.PI, 0, true);
    c.lineTo(8, -10);
    c.lineTo(3, -10);
    c.lineTo(3, -1);
    c.arc(0, -1, 3, 0, Math.PI, false);
    c.lineTo(-3, -10);
    c.lineTo(-8, -10);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    c.fillStyle = k;
    c.fillRect(-8, -10, 5, 3);
    c.fillRect(3, -10, 5, 3);
  },
  overdrive(c, f, k) {
    c.beginPath();
    c.arc(0, 4, 10, Math.PI, 0);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    stroke(c, [[0, 4], [6, -3]], k, 2.6);
    round(c, 0, 4, 2, k, k);
  },
  salvage(c, f, k) {
    shape(c, [[-9, -4], [9, -4], [9, 9], [-9, 9]], f, k);
    shape(c, [[-10, -9], [10, -9], [10, -4], [-10, -4]], f, k);
    stroke(c, [[0, -9], [0, 9]], k);
  },
  precision(c, f, k) {
    round(c, 0, 0, 8, f, k);
    round(c, 0, 0, 3, k, k);
    for (const [a, b] of [[[0, -12], [0, -6]], [[0, 12], [0, 6]], [[-12, 0], [-6, 0]], [[12, 0], [6, 0]]]) stroke(c, [a, b], k, 2.4);
  },
  execution(c, f, k) {
    c.beginPath();
    c.arc(0, -2, 8, Math.PI * 0.85, Math.PI * 2.15);
    c.lineTo(4, 9);
    c.lineTo(-4, 9);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    round(c, -3, -1, 2.2, k, k);
    round(c, 3, -1, 2.2, k, k);
  },
  resonance(c, f, k) {
    round(c, 0, 0, 3, f, k);
    for (const r of [7, 11]) {
      c.beginPath();
      c.arc(0, 0, r, -0.9, 0.9);
      c.strokeStyle = k;
      c.stroke();
      c.beginPath();
      c.arc(0, 0, r, Math.PI - 0.9, Math.PI + 0.9);
      c.stroke();
    }
  },
  tune(c, f, k) {
    GLYPHS.repair(c, f, k);
  },
  // HUD
  pause(c, f, k) {
    for (const x of [-5, 5]) shape(c, [[x - 2.6, -9], [x + 2.6, -9], [x + 2.6, 9], [x - 2.6, 9]], f, k);
  },
  left(c, f, k) {
    shape(c, [[5, -10], [-6, 0], [5, 10], [5, 5], [0, 0], [5, -5]], f, k);
  },
  right(c, f, k) {
    shape(c, [[-5, -10], [6, 0], [-5, 10], [-5, 5], [0, 0], [-5, -5]], f, k);
  },
  play(c, f, k) {
    shape(c, [[-6, -10], [10, 0], [-6, 10]], f, k);
  },
  exit(c, f, k) {
    shape(c, [[-9, -9], [2, -9], [2, 9], [-9, 9]], f, k);
    shape(c, [[4, -5], [11, 0], [4, 5]], f, k);
  },
  music(c, f, k) {
    stroke(c, [[-5, 8], [-5, -7], [8, -10], [8, 5]], k, 2.4);
    stroke(c, [[-5, -4], [8, -7]], k, 2.4);
    c.fillStyle = f;
    c.beginPath(); c.ellipse(-8, 8, 4, 3, -.2, 0, TAU); c.fill(); c.stroke();
    c.beginPath(); c.ellipse(5, 5, 4, 3, -.2, 0, TAU); c.fill(); c.stroke();
  },
  sound(c, f, k) {
    shape(c, [[-10, -4], [-5, -4], [1, -10], [1, 10], [-5, 4], [-10, 4]], f, k);
    for (const r of [5, 9]) {
      c.beginPath();
      c.arc(1, 0, r, -0.8, 0.8);
      c.strokeStyle = k;
      c.stroke();
    }
  },
  mute(c, f, k) {
    shape(c, [[-10, -4], [-5, -4], [1, -10], [1, 10], [-5, 4], [-10, 4]], f, k);
    stroke(c, [[5, -4], [11, 4]], k, 2.4);
    stroke(c, [[11, -4], [5, 4]], k, 2.4);
  },
  vibrate(c, f, k) {
    shape(c, [[-5, -10], [5, -10], [5, 10], [-5, 10]], f, k);
    for (const s of [-1, 1]) stroke(c, [[s * 8, -6], [s * 10, -3], [s * 8, 0], [s * 10, 3], [s * 8, 6]], k);
  },
  still(c, f, k) {
    shape(c, [[-5, -10], [5, -10], [5, 10], [-5, 10]], f, k);
    stroke(c, [[-10, 10], [10, -10]], k, 2.4);
  },
  ink(c, f, k) {
    c.beginPath();
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * TAU,
        r = i % 2 ? 6 : 11 - (i % 4) * 1.2;
      c.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    round(c, 0, 0, 3, k, k);
  },
  swap(c, f, k) {
    shape(c, [[-10, -3], [4, -3], [4, -8], [11, -1], [4, 6], [4, 1], [-10, 1]], f, k);
  },
  team(c, f, k) {
    round(c, -4, -4, 4, f, k);
    round(c, 5, -2, 3.4, f, k);
    shape(c, [[-11, 9], [-9, 2], [1, 2], [3, 9]], f, k);
  },
  reroll(c, f, k) {
    c.beginPath();
    c.arc(0, 0, 8, -2.4, 2.1);
    c.strokeStyle = k;
    c.lineWidth = 4;
    c.stroke();
    c.strokeStyle = f;
    c.lineWidth = 2;
    c.stroke();
    shape(c, [[-9, -9], [-3, -9], [-6, -3]], f, k);
  },
  boss(c, f, k) {
    c.beginPath();
    c.arc(0, -2, 9, Math.PI * 0.85, Math.PI * 2.15);
    c.lineTo(5, 10);
    c.lineTo(-5, 10);
    c.closePath();
    c.fillStyle = f;
    c.fill();
    c.strokeStyle = k;
    c.stroke();
    shape(c, [[-7, -3], [-2, -1], [-5, 2]], k, k);
    shape(c, [[7, -3], [2, -1], [5, 2]], k, k);
    for (const x of [-2, 2]) stroke(c, [[x, 6], [x, 10]], k);
  },
  clock(c, f, k) {
    round(c, 0, 0, 10, f, k);
    stroke(c, [[0, -6], [0, 0], [5, 3]], k, 2.4);
  },
  star(c, f, k) {
    star(c, f, k);
  },
  plus(c, f, k) {
    shape(c, [[-3, -10], [3, -10], [3, -3], [10, -3], [10, 3], [3, 3], [3, 10], [-3, 10], [-3, 3], [-10, 3], [-10, -3], [-3, -3]], f, k);
  },
  power(c, f, k) {
    bolt(c, f, k);
  },
};

/** Picks a glyph for any upgrade id (tune-/evolve- fall back to the weapon). */
export function upgradeGlyph(id: string) {
  if (id.startsWith("tune-") || id.startsWith("evolve-")) return id.split("-")[1];
  return GLYPHS[id] ? id : "star";
}
export function icon(c: Ctx, name: string, x: number, y: number, size: number, fill = "#FFFFFF", ink = INK) {
  const g = GLYPHS[name] ?? GLYPHS.star;
  c.save();
  c.translate(x, y);
  c.scale(size / 24, size / 24);
  c.lineWidth = 2;
  c.lineJoin = "round";
  c.lineCap = "round";
  g(c, fill, ink);
  c.restore();
}
